/**
 * Secondary G1 stream page content: semantics + FAQ + decision-support data
 * plus the thin builder that runs the shared generator and attaches them.
 */
import { buildStreamPageContent } from './secondaryStreamGenerator';
import type { DecisionProofPoint, FaqItem, LevelPageContent } from './types';

const G1_SEMANTICS: Record<string, any> = {
  keySuffix: 'G1',
  streamName: 'G1 stream',
  seoLabel: 'G1',
  eyebrowLabel: 'G1 MATHEMATICS',
  lower: {
    seoDescription:
      'The MOE G1 Mathematics syllabus for Lower Secondary, topic by topic — plus weekly live classes that build secure foundations one step at a time.',
    headline: 'Build confidence through clear, step-by-step foundations.',
    subheadFocus:
      'practical numeracy, introductory algebra, and confidence with structured problem solving.',
    syllabusBody:
      'Content is scaffolded carefully so students strengthen fundamentals before moving into abstract application.',
    syllabusTopics: [
      {
        category: 'Foundational Number & Algebra',
        items: [
          'Integers, fractions, decimals, and percentages',
          'Simple algebraic expressions and equations',
          'Ratio, rate, and basic problem translation',
        ],
      },
      {
        category: 'Geometry & Measurement Foundations',
        items: [
          'Angles, properties of shapes, and polygons',
          'Perimeter, area, and volume in practical settings',
          'Reading simple coordinate relationships',
        ],
      },
      {
        category: 'Data & Practical Problem Solving',
        items: [
          'Interpreting tables, charts, and graphs',
          'Mean, median, mode, and data comparison',
          'Breaking down everyday multi-step questions',
        ],
      },
    ],
    syllabusNote:
      'Lower Secondary G1 success depends on confidence and consistency. We prioritise secure mastery over rushing content.',
    challengeFocusTitle: 'Rebuilding confidence after earlier setbacks',
    challengeFocusDescription:
      'Many students in this pathway need to re-establish belief in their Math ability while building missing foundations.',
    streamSupportTitle: 'Step-by-step reinforcement at the right pace',
    streamSupportDescription:
      'Practice and teaching are calibrated to remove overwhelm, strengthen weak basics, and keep progress visible.',
    progressionGoal:
      'transition into Upper Secondary with secure foundations and greater confidence.',
  },
  upper: {
    seoDescription:
      'The MOE G1 Mathematics syllabus for Upper Secondary, topic by topic — plus weekly live classes, focused revision, and N-Level exam preparation.',
    headline: 'Prepare steadily for N-Level with focused, manageable support.',
    subheadFocus:
      'consolidation of core concepts, practical application, and calm exam preparation.',
    syllabusBody:
      'Lessons target high-impact N-Level outcomes while continuing to reinforce prerequisite concepts from earlier years.',
    syllabusTopics: [
      {
        category: 'N-Level Core Number & Algebra',
        items: [
          'Core operations, ratio, and percentage fluency',
          'Linear equations and practical formula use',
          'Function interpretation and graph basics',
        ],
      },
      {
        category: 'Geometry, Mensuration & Trigonometry Basics',
        items: [
          'Angles, congruence, and geometric reasoning',
          'Area, volume, and real-world mensuration',
          'Pythagoras and basic trigonometric application',
        ],
      },
      {
        category: 'Exam Application & Revision',
        items: [
          'Interpreting exam-style word problems',
          'Prioritising marks through question strategy',
          'Timed practice and confidence under pressure',
        ],
      },
    ],
    syllabusNote:
      'Upper Secondary G1 preparation balances concept reinforcement with practical exam strategy for N-Level readiness.',
    challengeFocusTitle: 'Closing long-standing gaps before exams',
    challengeFocusDescription:
      'Students often need to strengthen older weak topics while simultaneously preparing for timed exam papers.',
    streamSupportTitle: 'Structured revision cycles for exam readiness',
    streamSupportDescription:
      'We sequence revision to reinforce weak chapters, build familiarity with question patterns, and reduce exam anxiety.',
    progressionGoal:
      'approach N-Level with clarity, confidence, and dependable exam habits.',
  },
};

const G1_FAQS: Record<string, FaqItem[]> = {
  '1-g1': [
    {
      question: 'What does G1 Math cover in Secondary 1?',
      answer:
        'Sec 1 G1 covers foundational number work, basic algebra, introductory geometry, and simple data handling. The pace is deliberately supportive, building confidence with each concept before moving on.',
    },
    {
      question: 'Can my Sec 1 G1 child move to G2 later?',
      answer:
        'Yes. Under Subject-Based Banding, students can move between G1, G2, and G3 based on school assessments. Building strong G1 foundations in Sec 1 makes an upward move more achievable.',
    },
    {
      question: 'Is G1 Math too easy for my child?',
      answer:
        'G1 is not about difficulty — it is about pacing and scaffolding. If your child finds it comfortable, that is a sign the foundations are forming well, which supports future progression to G2.',
    },
    {
      question: 'How is Sec 1 G1 different from Primary 6 Math?',
      answer:
        'G1 introduces formal algebra, negative numbers, and systematic problem-solving. While it builds on primary concepts, the mathematical thinking expected is more structured and abstract.',
    },
    {
      question:
        'My child struggles with Math — is Sec 1 too early for tuition?',
      answer:
        'Sec 1 is actually the best time to start. Gaps in foundational concepts compound quickly in secondary school, so early intervention prevents larger problems in Sec 2 and beyond.',
    },
    {
      question: 'What is the SEC exam and how does it affect my G1 child?',
      answer:
        'The Singapore-Cambridge SEC replaces all three national secondary exams (O-Level, N(A)-Level, N(T)-Level) from 2027. Students sit one unified exam with each subject taken at G1, G2, or G3 level. G1 Math aligns to the former N(T) standard. Your child receives a single SEC certificate showing every subject with its level and grade.',
    },
  ],
  '2-g1': [
    {
      question: 'What does Sec 2 G1 Math cover?',
      answer:
        "Sec 2 G1 covers triangles and quadrilaterals, linear algebraic expressions and equations, functions and graphs, proportion, rate and speed, congruence and similarity, Pythagoras' theorem, data analysis, probability, and mensuration.",
    },
    {
      question: 'My child struggled in Sec 1 G1 — is it too late to catch up?',
      answer:
        'Sec 2 is a consolidation year, which actually makes it ideal for catching up. With targeted support, students can close Sec 1 gaps while keeping pace with Sec 2 content before Upper Secondary begins.',
    },
    {
      question: 'What happens after Sec 2 G1 — which exams does my child sit?',
      answer:
        "G1 students typically prepare for national examinations at the end of Sec 4. Strong Sec 2 G1 performance can also support a school's decision to offer lateral transfer to G2 for broader exam options.",
    },
    {
      question: 'How important is Sec 2 for G1 students specifically?',
      answer:
        'Sec 2 is the last year before Upper Secondary content intensifies. G1 students who enter Sec 3 with unresolved gaps in algebra, ratios, or equation-solving find it very difficult to recover during exam preparation years.',
    },
    {
      question: 'Can my Sec 2 G1 child still move to G2?',
      answer:
        'Yes, Subject-Based Banding allows movement between streams. Schools assess readiness based on sustained performance. Consistent support in Sec 2 can build the foundations needed for a successful transition.',
    },
    {
      question: 'What exam will my Sec 2 G1 child eventually sit?',
      answer:
        'Current Sec 2 students will sit the Singapore-Cambridge SEC in Sec 4 — a single national exam replacing all three former leaving exams (O-Level, N(A)-Level, N(T)-Level). G1 Math aligns to the former N(T) standard. Students receive one unified certificate showing every subject with its G-level and grade.',
    },
  ],
  '3-g1': [
    {
      question: 'What does Sec 3 G1 Math cover?',
      answer:
        'Sec 3 G1 covers indices and standard form, map scales, algebraic expansion and factorisation, arc lengths and sector areas, pyramids, cones and spheres, cumulative frequency, linear graphs, and simultaneous equations.',
    },
    {
      question: 'How does Sec 3 G1 prepare for national exams?',
      answer:
        'Sec 3 introduces the remaining syllabus topics. Mastering these in Sec 3 gives students a full year of revision and practice time in Sec 4, rather than learning and revising simultaneously.',
    },
    {
      question:
        'Is it too late for a Sec 3 G1 student to improve significantly?',
      answer:
        'Not at all. Sec 3 is often when students mature and become more motivated. With structured support, many G1 students make significant improvements when they engage consistently with the right kind of practice.',
    },
    {
      question: 'Can a Sec 3 G1 student still move to G2?',
      answer:
        'Lateral movement is possible but becomes harder in Upper Secondary because the curriculum gap widens. Students considering this should start closing the gap early in Sec 3 with targeted support.',
    },
    {
      question: 'What should Sec 3 G1 students prioritise?',
      answer:
        'Algebra and equation-solving fluency. These skills underpin nearly every exam question. Students who secure these in Sec 3 find Sec 4 revision far more manageable and productive.',
    },
    {
      question: 'Will my Sec 3 G1 child sit the SEC exam or N-Levels?',
      answer:
        'Current Sec 3 students are the first cohort sitting the Singapore-Cambridge SEC in 2027 — a single national exam replacing O-Levels, N(A)-Levels, and N(T)-Levels. G1 Math aligns to the former N(T) standard. All subjects appear on one unified certificate, and students can mix G-levels across subjects.',
    },
  ],
  '4-g1': [
    {
      question: 'What exams does a Sec 4 G1 / Normal Technical student sit?',
      answer:
        'G1 (Normal Technical) students sit the N(T)-Level Mathematics examination at the end of Sec 4. Strong results can open pathways to ITE courses and, with further study, polytechnic admission.',
    },
    {
      question: 'Is Sec 4 too late to start tuition for G1 / NT Math?',
      answer:
        'It is not too late, but time is limited. Focused support that prioritises high-impact topics and exam technique can still produce meaningful improvement in the months before the N(T)-Level exam.',
    },
    {
      question:
        'What should a Sec 4 G1 / Normal Technical student focus on for N(T)-Level?',
      answer:
        'Prioritise algebra, mensuration, and data analysis — these topics appear consistently in N(T)-Level papers. Strong performance on predictable question types can significantly improve overall grades.',
    },
    {
      question: 'How is the N(T)-Level Math exam structured?',
      answer:
        'The N(T)-Level Math paper tests practical numeracy, basic algebra, geometry, and data interpretation. Questions tend to be application-focused, testing whether students can use Math in everyday contexts.',
    },
    {
      question: 'Can a Sec 4 G1 / NT student still move to G2 / NA for Sec 5?',
      answer:
        "Students who perform well in N(T)-Level can progress to a Sec 5 N(A)-Level pathway or apply to ITE. The decision depends on school policy and the student's overall performance across subjects.",
    },
  ],
};

const G1_DECISION: Record<string, DecisionProofPoint[]> = {
  '1-g1': [
    {
      title: 'Your child found primary Math stressful',
      description:
        'Students entering G1 after a difficult PSLE experience need support that rebuilds confidence alongside content — not more of the same pressure that made Math feel hard.',
    },
    {
      title: 'Homework is taking much longer than expected',
      description:
        'If simple Sec 1 G1 assignments are consuming hours, it usually signals gaps in foundational skills like number bonds, fractions, or basic equation-solving that need targeted attention.',
    },
    {
      title:
        "Your child says they \"don't understand\" but can't explain what's confusing",
      description:
        'This is common when students lack the mathematical vocabulary and framework to articulate their difficulties. Structured teaching gives them the language and confidence to identify and address their own gaps.',
    },
  ],
  '2-g1': [
    {
      title: "Your child's Sec 1 results were weaker than expected",
      description:
        'Sec 2 is the consolidation year — the last chance to fix Sec 1 gaps before Upper Secondary content intensifies. Targeted support now prevents these gaps from becoming permanent obstacles.',
    },
    {
      title: 'Your child is unsure whether G1 is the right level',
      description:
        'If your child feels the work is too easy or too hard, diagnostic assessment can clarify exactly where they stand and whether targeted support could enable a stream change.',
    },
    {
      title:
        'You want your child to have the best possible options after Sec 2',
      description:
        'Strong Sec 2 G1 performance can open pathways to G2 or better preparation for N(T)-Level success. Consistent support during this year directly influences post-secondary options.',
    },
  ],
  '3-g1': [
    {
      title: 'Your child is entering Upper Secondary and feels unprepared',
      description:
        'The Sec 3 content jump is demanding even for well-prepared students. G1 students entering Upper Secondary with Sec 1-2 gaps need targeted support to manage new topics alongside remediation.',
    },
    {
      title: 'N(T)-Level preparation feels overwhelming',
      description:
        'The N(T)-Level exam covers the full Sec 1-4 syllabus. Starting structured exam preparation in Sec 3 gives your child time to master topics properly rather than cramming in Sec 4.',
    },
    {
      title: 'Your child has matured and is ready to put in more effort',
      description:
        'Many students become more motivated in Upper Secondary. Structured support channels this motivation into productive study habits and tangible improvement.',
    },
  ],
  '4-g1': [
    {
      title:
        'The N(T)-Level exam is approaching and your Normal Technical / G1 child is not ready',
      description:
        'Even in the final year, focused revision of high-impact topics can produce meaningful improvement. The key is prioritising — not trying to cover everything, but mastering what matters most.',
    },
    {
      title: 'Your child lacks exam technique and time management skills',
      description:
        'N(T)-Level papers test practical application under time constraints. Many Normal Technical / G1 students lose marks not because they cannot do the Math, but because they have not practised exam-style execution.',
    },
    {
      title: 'Your child wants to do well enough for post-secondary options',
      description:
        'N(T)-Level Math results influence ITE course options and future pathways. Structured exam preparation gives your child the best chance of achieving the grades they need.',
    },
  ],
};

export function buildSecondaryG1Content(
  secLevel: number,
  brandName = 'DeepThink'
): LevelPageContent {
  const content = buildStreamPageContent(
    secLevel,
    G1_SEMANTICS,
    'g1',
    brandName
  );
  const dataKey = `${secLevel}-g1`;
  return {
    ...content,
    ...(G1_FAQS[dataKey] && { faqItems: G1_FAQS[dataKey] }),
    ...(G1_DECISION[dataKey] && {
      decisionProofPoints: G1_DECISION[dataKey],
    }),
  };
}
